/* eslint-disable no-underscore-dangle */
import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DEFAULT_THEME_COLOR } from 'appenv';
import { Booth } from 'models/cms/booths';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import isGroupVideoCallDisabled from 'components/chat/utils/isGroupVideoCallDisabled';
import { TabsContext } from 'components/TabsContext';
import useEncodedMeetupUrl from 'hooks/useEncodedMeetupUrl';
import VideoCallIcon from 'components/icons/VideoCall';
import InsertInvitationIcon from 'components/icons/Calendar';
import EmailIcon from 'components/icons/EmailSmall';
import ChatroomIcon from 'components/icons/Chatroom';
import useJoinGroupChatSwitch from 'hooks/useJoinGroupChatSwitch';
import useGroupVideoCallSwitch from 'hooks/useGroupVideoCallSwitch';
import { LocalizableString } from 'models/cms/common/types';
import { BoothContext } from '../BoothProfileContainer';

export enum WidgetAction {
  Chat = 'CHAT',
  Meetup = 'MEETUP',
  ExternalLink = 'EXTERNAL_LINK',
  LocalizableExternalLinkUrl = 'LOCALIZABLE_EXTERNAL_LINK',
  ContactUs = 'CONTACT_US',
  FileTab = 'FILE_TAB',
  LiveStream = 'LIVE_STREAM',
  VideoCall = 'VIDEO_CALL'
}

export interface NetworkingWidgetItem {
  id: number;
  color: string;
  message: string;
  actionType: WidgetAction;
  IconComponent: any;
}

const getMailToLinkFromTabs = (tabs: any[], locale: string) => {
  const emailContacts: Array<any> = tabs
    .flatMap((it) => it?.body)
    .filter((it) => it?.__typename === 'BoothQuestionContentElement' && it?.answer?.contentRichtextHtml?.[locale])
    .map((it) => ({
      type: 'email',
      contact: it?.answer?.contentRichtextHtml?.[locale]?.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi)?.[0],
    }))
    .filter((it) => it?.contact);

  if (emailContacts && emailContacts.length > 0) {
    const email = emailContacts.find((eachEmailContact) => (eachEmailContact.type === 'email'));
    if (email?.contact) {
      return `mailto:${email.contact}`;
    }
  }
  return '';
};

const checkIsEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const useNetworkingWidget = () => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const { hall, themeColor } = useSelector((s: any) => s.eventConfigs);
  const resolvedColor = themeColor || DEFAULT_THEME_COLOR;

  const greetingMessage = useMemo(() => findAvailableLocale(hall?.popOver?.boothGreeting, locale) || t('', 'Hi, feel free to talk to us!'), [hall?.popOver?.boothGreeting, locale, t]);
  const chatMessage = useMemo(() => findAvailableLocale(hall?.popOver?.chat, locale) || t('booth.chat', 'Chat'), [hall?.popOver?.chat, locale, t]);
  const contactUsMessage = useMemo(() => findAvailableLocale(hall?.popOver?.mailTo, locale) || t('booth.leave_a_message', 'Leave a Message'), [hall?.popOver?.mailTo, locale, t]);
  const meetupMessage = useMemo(() => findAvailableLocale(hall?.popOver?.meetup, locale) || t('booth.meetup', 'Meetup'), [hall?.popOver?.meetup, locale, t]);
  const videoCallMessage = useMemo(() => findAvailableLocale(hall?.popOver?.videoCall, locale) || t('booth.video_call', 'Video Call'), [hall?.popOver?.videoCall, locale, t]);

  const items: NetworkingWidgetItem[] = useMemo(() => ([
    {
      id: 0,
      color: resolvedColor,
      message: chatMessage,
      actionType: WidgetAction.Chat,
      IconComponent: ChatroomIcon,
    },
    {
      id: 1,
      color: resolvedColor,
      message: contactUsMessage,
      actionType: WidgetAction.ContactUs,
      IconComponent: EmailIcon,
    },
    {
      id: 2,
      color: resolvedColor,
      message: meetupMessage,
      actionType: WidgetAction.Meetup,
      IconComponent: InsertInvitationIcon,
    },
    {
      id: 3,
      color: resolvedColor,
      message: videoCallMessage,
      actionType: WidgetAction.VideoCall,
      IconComponent: VideoCallIcon,
    },
  ]), [chatMessage, contactUsMessage, meetupMessage, videoCallMessage, resolvedColor]);

  return { greetingMessage, items };
};

interface UseNetworkingWidgetItemLayer {
  actionType?: string;
  actionExternalLinkUrl?: string;
  actionLocalizableExternalLinkUrl?: LocalizableString
}

export const useNetworkingWidgetLogic = (booth: Booth) => {
  const locale = useSelector(selectLocale);
  const { chatroomConfig, features } = useSelector((state: any) => (state.eventConfigs));
  const { setActiveTabKey, tabs } = useContext(TabsContext);
  const { boothNameForMatomo } = useContext(BoothContext);
  const { joinBoothChat } = useJoinGroupChatSwitch();
  const createGroupVideoCall = useGroupVideoCallSwitch();

  const {
    id: boothId,
    hasAttachment,
    meetupEnabled,
    appointmentBookingUrl,
  } = booth || {};
  const encodedMeetupUrl = useEncodedMeetupUrl(appointmentBookingUrl);
  const boothName = booth?.name?.[locale];
  const mailToLink = useMemo(() => getMailToLinkFromTabs(tabs, locale), [tabs, locale]);
  const liveStreamTabId = booth?.livestreams?.[0]?.tabId;
  const liveStreamTabIndex = useMemo(() => (tabs.findIndex((tab) => tab.id === liveStreamTabId)), [liveStreamTabId, tabs]);
  const attachmentTabIndex = useMemo(() => tabs.findIndex(({ masterElement, body }) => (
    body?.some((element) => masterElement?.masterControlledBody === false || (element?.__typename === 'BoothFileContentElement' && element?.file?.[locale]?.fileUrl))
  )), [locale, tabs]);

  const checkIfItemDisabled = useCallback((layer: UseNetworkingWidgetItemLayer) => {
    const { actionType, actionExternalLinkUrl, actionLocalizableExternalLinkUrl } = layer || {};
    switch (actionType) {
      case WidgetAction.Chat:
        return isChatroomDisabled(chatroomConfig);
      case WidgetAction.Meetup:
        return !meetupEnabled;
      case WidgetAction.ExternalLink:
        return !actionExternalLinkUrl;
      case WidgetAction.LocalizableExternalLinkUrl:
        return !findAvailableLocale(actionLocalizableExternalLinkUrl, locale);
      case WidgetAction.ContactUs:
        return !mailToLink;
      case WidgetAction.FileTab:
        return !hasAttachment || [-1, undefined, null].includes(attachmentTabIndex);
      case WidgetAction.LiveStream:
        return [-1, undefined, null].includes(liveStreamTabIndex);
      case WidgetAction.VideoCall:
        return !features.includes('groupVideoCall') || isGroupVideoCallDisabled(chatroomConfig);
      default:
        return true;
    }
  }, [attachmentTabIndex, chatroomConfig, features, hasAttachment, liveStreamTabIndex, mailToLink, meetupEnabled, locale]);

  const buildItemClickHandler = useCallback((layer: UseNetworkingWidgetItemLayer) => () => {
    const { actionType, actionExternalLinkUrl, actionLocalizableExternalLinkUrl } = layer || {};
    const scrollDown = () => {
      document.getElementById('booth-container').scroll({ top: 1000, left: 0, behavior: 'smooth' });
    };

    switch (actionType) {
      case WidgetAction.Chat: {
        window.trackingItemToFirebase(null, 'Booth', 'Chat', 'Group_Chat_Click', 'Booth_Profile', boothId, boothNameForMatomo);
        const isAutoJoinMsgDisabled = chatroomConfig?.isAutoJoinMsgDisabled;
        joinBoothChat(boothId, boothName, !isAutoJoinMsgDisabled);
        break;
      }
      case WidgetAction.Meetup: {
        window.trackingItemToFirebase(null, 'Booth', 'Meetup', 'Meetup_Click', 'Booth_Profile', boothId, boothNameForMatomo);
        window.open(encodedMeetupUrl, '_blank');
        break;
      }
      case WidgetAction.ExternalLink: {
        let link = actionExternalLinkUrl;
        if (checkIsEmail(actionExternalLinkUrl)) {
          link = `mailto:${actionExternalLinkUrl}`;
        } else if (actionExternalLinkUrl.startsWith('http://') || actionExternalLinkUrl.startsWith('https://')) {
          link = actionExternalLinkUrl;
        } else {
          link = `//${actionExternalLinkUrl}`;
        }
        window.open(link, '_blank');
        break;
      }
      case WidgetAction.LocalizableExternalLinkUrl: {
        let link = findAvailableLocale(actionLocalizableExternalLinkUrl, locale);
        if (checkIsEmail(link)) {
          link = `mailto:${link}`;
        } else if (!(link.startsWith('http://') || link.startsWith('https://'))) {
          link = `//${link}`;
        }
        window.open(link, '_blank');
        break;
      }
      case WidgetAction.ContactUs: {
        window.trackingItemToFirebase(null, 'Booth', 'Leave a Message', 'Leave a Message_Click', 'Booth_Profile', boothId, boothNameForMatomo);
        window.open(mailToLink, '_blank');
        break;
      }
      case WidgetAction.FileTab: {
        setActiveTabKey(attachmentTabIndex);
        scrollDown();
        break;
      }
      case WidgetAction.LiveStream: {
        setActiveTabKey(liveStreamTabIndex);
        scrollDown();
        break;
      }
      case WidgetAction.VideoCall: {
        createGroupVideoCall(boothName, boothId);
        break;
      }
      default:
        break;
    }
  }, [
    boothId,
    boothName,
    boothNameForMatomo,
    mailToLink,
    attachmentTabIndex,
    liveStreamTabIndex,
    setActiveTabKey,
    createGroupVideoCall,
    locale,
    joinBoothChat,
    chatroomConfig?.isAutoJoinMsgDisabled,
    encodedMeetupUrl,
  ]);

  return {
    checkIfItemDisabled,
    buildItemClickHandler,
  };
};

export const useNetworkingWidgetItem = (layer: UseNetworkingWidgetItemLayer, booth: Booth) => {
  const { checkIfItemDisabled, buildItemClickHandler } = useNetworkingWidgetLogic(booth);
  const calculatedFields = useMemo(() => ({
    isItemDisabled: checkIfItemDisabled(layer),
    handleItemClick: buildItemClickHandler(layer),
  }), [layer, checkIfItemDisabled, buildItemClickHandler]);
  return calculatedFields;
};
